import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import useItemStore from '@store/useItemStore'
import itemService from '@services/itemService'
import { Modal, Button, Input, Select } from '@components/common'
import { Upload, X, FileText } from 'lucide-react'

/**
 * ItemForm Component
 *
 * Modal form for creating and editing items in the repository.
 * Supports file upload with drag and drop.
 */
const ItemForm = ({ mode = 'create', onClose }) => {
  const { selectedItem, createItem, updateItem, loading } = useItemStore()
  const fileInputRef = useRef(null)

  const [formData, setFormData] = useState({
    title: selectedItem?.title || '',
    description: selectedItem?.description || '',
    item_type: selectedItem?.item_type || 'resource',
    status: selectedItem?.status || 'draft',
    change_reason: '',
  })
  const [file, setFile] = useState(null)
  const [errors, setErrors] = useState({})
  const [isDragOver, setIsDragOver] = useState(false)

  const isEdit = mode === 'edit'

  /**
   * Handle input change
   */
  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }))
    }
  }

  /**
   * Handle file selection
   */
  const handleFileSelect = (selectedFile) => {
    const validation = itemService.validateFile(selectedFile)
    if (!validation.valid) {
      setErrors((prev) => ({ ...prev, file: validation.errors.join(', ') }))
      return
    }
    setFile(selectedFile)
    setErrors((prev) => ({ ...prev, file: null }))
  }

  /**
   * Handle file input change
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      handleFileSelect(selectedFile)
    }
  }

  /**
   * Handle drag and drop
   */
  const handleDragOver = (e) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setIsDragOver(false)
    const droppedFile = e.dataTransfer.files[0]
    if (droppedFile) {
      handleFileSelect(droppedFile)
    }
  }

  /**
   * Remove selected file
   */
  const handleRemoveFile = () => {
    setFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  /**
   * Validate form
   */
  const validateForm = () => {
    const newErrors = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title cannot exceed 255 characters'
    }

    if (!isEdit && !file) {
      newErrors.file = 'File is required'
    }

    if (isEdit && file) {
      const validation = itemService.validateFile(file)
      if (!validation.valid) {
        newErrors.file = validation.errors.join(', ')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submit
   */
  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) return

    const submitData = new FormData()
    submitData.append('title', formData.title)
    submitData.append('description', formData.description || '')
    submitData.append('item_type', formData.item_type)
    submitData.append('status', formData.status)

    if (file) {
      submitData.append('file', file)
    }

    if (isEdit && formData.change_reason) {
      submitData.append('change_reason', formData.change_reason)
    }

    let result
    if (isEdit) {
      result = await updateItem(selectedItem.id, submitData)
    } else {
      result = await createItem(submitData)
    }

    if (result) {
      onClose()
    }
  }

  const typeOptions = itemService.getItemTypes().map((t) => ({
    value: t.value,
    label: t.label,
  }))

  const statusOptions = itemService.getStatusOptions().map((s) => ({
    value: s.value,
    label: s.label,
  }))

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isEdit ? 'Edit Item' : 'Add New Item'}
      size="medium"
      closeOnBackdrop={false}
    >
      <form onSubmit={handleSubmit} className="item-form">
        {/* Title */}
        <div className="form-group">
          <label htmlFor="title">Title *</label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="Enter item title"
            error={errors.title}
          />
        </div>

        {/* Description */}
        <div className="form-group">
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Enter item description (optional)"
            rows={3}
            className="form-textarea"
          />
        </div>

        {/* Type */}
        <div className="form-group">
          <label htmlFor="item_type">Type *</label>
          <Select
            id="item_type"
            name="item_type"
            value={formData.item_type}
            onChange={handleChange}
            options={typeOptions}
          />
        </div>

        {/* Status */}
        <div className="form-group">
          <label htmlFor="status">Status *</label>
          <Select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={statusOptions}
          />
          <p className="form-group__hint">
            Only published items can be assigned to companies.
          </p>
        </div>

        {/* File Upload */}
        <div className="form-group">
          <label>File {!isEdit && '*'}</label>
          <div
            className={`file-upload ${isDragOver ? 'file-upload--dragover' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleFileChange}
            />
            <div className="file-upload__icon">
              <Upload size={32} />
            </div>
            <p className="file-upload__text">
              {isEdit ? 'Upload new file to create a new version' : 'Drag and drop a file here, or click to select'}
            </p>
            <p className="file-upload__hint">
              Allowed formats: PDF, DOC, DOCX, XLS, XLSX (Max 10MB)
            </p>
          </div>
          {errors.file && <p className="error-text">{errors.file}</p>}

          {/* File Preview */}
          {file && (
            <div className="file-preview">
              <div className="file-preview__icon">
                <FileText size={20} />
              </div>
              <div className="file-preview__info">
                <div className="file-preview__name">{file.name}</div>
                <div className="file-preview__size">{itemService.formatFileSize(file.size)}</div>
              </div>
              <button
                type="button"
                className="file-preview__remove"
                onClick={handleRemoveFile}
              >
                <X size={20} />
              </button>
            </div>
          )}

          {/* Current File (Edit mode) */}
          {isEdit && !file && selectedItem?.original_name && (
            <div className="file-preview">
              <div className="file-preview__icon">
                <FileText size={20} />
              </div>
              <div className="file-preview__info">
                <div className="file-preview__name">{selectedItem.original_name}</div>
                <div className="file-preview__size">
                  {itemService.formatFileSize(selectedItem.file_size)} • Current file (v{selectedItem.current_version})
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Change Reason (Edit mode with file) */}
        {isEdit && file && (
          <div className="form-group">
            <label htmlFor="change_reason">Change Reason</label>
            <textarea
              id="change_reason"
              name="change_reason"
              value={formData.change_reason}
              onChange={handleChange}
              placeholder="Describe what changed in this version..."
              rows={2}
              className="form-textarea"
            />
            <p className="form-group__hint">
              This will be recorded in the version history.
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="modal-actions">
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Item' : 'Create Item'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

ItemForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  onClose: PropTypes.func.isRequired,
}

export default ItemForm
